import { useState, useEffect, useCallback, useRef } from 'react';
import { Box } from '@mui/material';
import B2BProformaTable from './B2BProformaTable';
import AddProformaModal from './AddProformaModal';
import FranchiseProformaRequestModal from './FranchiseProformaRequestModal';
import EditProformaQuantitiesModal from './EditProformaQuantitiesModal';
import AcceptProformaModal from './AcceptProformaModal';
import ViewProformaModal from './ViewProformaModal';
import PDFViewerModal from './PDFViewerModal';
import RejectProformaModal from './RejectProformaModal';
import SendProformaEmailModal from './SendProformaEmailModal';
import PageHeader from '../../../components/PageHeader/PageHeader';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import InvoicePDFViewerModal from '../orders/PDFViewerModal';
import { B2BProformaService, B2BOrderService } from '../../../services/B2BServices';
import SnackbarUtils from '../../../utils/SnackbarUtils';
import { useWebSocket } from '../../../context/WebSocketContext';

const B2BProforma = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const destinationLocationId = searchParams.get('destinationLocationId');
  const destinationLocationName = searchParams.get('destinationLocationName');
  const isDestinationScopedView = Boolean(destinationLocationId);

  const [proformas, setProformas] = useState([]);
  const [selectedProforma, setSelectedProforma] = useState(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isAcceptModalOpen, setIsAcceptModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isPDFModalOpen, setIsPDFModalOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState('');
  const [invoiceUrl, setInvoiceUrl] = useState('');
  const [loadingInvoiceId, setLoadingInvoiceId] = useState(null);
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [isSendEmailModalOpen, setIsSendEmailModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // Estados para paginado
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [totalRows, setTotalRows] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortModel, setSortModel] = useState([]);
  const [filterModel, setFilterModel] = useState({ items: [] });
  // Por defecto solo se listan las pendientes; '' muestra todas.
  const [statusFilter, setStatusFilter] = useState('SENT');
  const searchTimeoutRef = useRef(null);

  // Datos de ejemplo para la demo
  const mockProformas = [
    {
      id: 1,
      proformaNumber: 'PROF-001',
      clientName: 'Empresa ABC S.A.',
      status: 'sent',
      issueDate: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(), // 5 días atrás
      validityDays: 30,
      totalAmount: 35000,
      items: [
        { productName: 'Producto A', quantity: 10, unitPrice: 1500, totalPrice: 15000, discountType: 'percentage', discountValue: 5 },
        { productName: 'Producto B', quantity: 5, unitPrice: 2000, totalPrice: 10000, discountType: 'amount', discountValue: 500 },
        { productName: 'Producto C', quantity: 8, unitPrice: 1250, totalPrice: 10000, discountType: 'percentage', discountValue: 0 }
      ],
      notes: 'Cotización para pedido mensual'
    },
    {
      id: 2,
      proformaNumber: 'PROF-002',
      clientName: 'Comercial XYZ Ltda.',
      status: 'accepted',
      issueDate: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString(), // 10 días atrás
      validityDays: 15,
      totalAmount: 28000,
      items: [
        { productName: 'Producto D', quantity: 15, unitPrice: 1200, totalPrice: 18000, discountType: 'percentage', discountValue: 10 },
        { productName: 'Producto E', quantity: 10, unitPrice: 1000, totalPrice: 10000, discountType: 'percentage', discountValue: 10 }
      ],
      notes: 'Cotización aprobada'
    },
    {
      id: 3,
      proformaNumber: 'PROF-003',
      clientName: 'Distribuidora Norte',
      status: 'expired',
      issueDate: new Date(Date.now() - 20 * 24 * 60 * 60 * 1000).toISOString(), // 20 días atrás
      validityDays: 7,
      totalAmount: 15000,
      items: [
        { productName: 'Producto F', quantity: 12, unitPrice: 1250, totalPrice: 15000, discountType: 'percentage', discountValue: 0 }
      ],
      notes: 'Cotización vencida'
    }
  ];

  // Función para verificar si una proforma está vencida
  const isProformaExpired = (proforma) => {
    if (proforma.status === 'accepted' || proforma.status === 'expired') {
      return proforma.status === 'expired';
    }
    
    const issueDate = new Date(proforma.issueDate);
    const expirationDate = new Date(issueDate.getTime() + (proforma.validityDays * 24 * 60 * 60 * 1000));
    return new Date() > expirationDate;
  };

  const fetchProformas = useCallback(async (
    page,
    pageSize,
    search,
    currentSortModel = sortModel,
    currentFilterModel = filterModel,
    { silent = false } = {}
  ) => {
    try {
      // Un refresco silencioso (por tiempo real o por reloj) actualiza la lista sin el spinner ni los datos de ejemplo.
      if (!silent) setLoading(true);
      const activeSort = Array.isArray(currentSortModel) ? currentSortModel[0] : null;
      const activeFilter = currentFilterModel?.items?.find((item) => item?.field && item?.operator);
      const response = await B2BProformaService.getProformas(page, pageSize, search, statusFilter, {
        sortField: activeSort?.field,
        sortDirection: activeSort?.sort,
        filterField: activeFilter?.field,
        filterOperator: activeFilter?.operator,
        filterValue: activeFilter?.value,
        destinationLocationId
      });
      
      const proformasData = response.proformas || response.data?.proformas || [];
      const totalCount = response.totalCount || response.data?.totalCount || 0;
      
      setProformas(proformasData);
      setTotalRows(totalCount);
    } catch (error) {
      console.error('Error fetching B2B proformas:', error);
      if (silent) return;
      // En caso de error, usar datos mock para desarrollo
      setProformas(mockProformas);
      setTotalRows(mockProformas.length);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [sortModel, filterModel, destinationLocationId, statusFilter]);

  // El temporizador de la búsqueda tiene que usar siempre la versión más nueva de fetchProformas: con la que tenía al
  // escribir, una búsqueda pendiente pisaba con resultados viejos un filtro de estado cambiado en el medio.
  const fetchProformasRef = useRef(fetchProformas);
  fetchProformasRef.current = fetchProformas;

  useEffect(() => {
    fetchProformas(paginationModel.page + 1, paginationModel.pageSize, searchTerm, sortModel, filterModel);
  }, [fetchProformas, paginationModel.page, paginationModel.pageSize, sortModel, filterModel]);

  // Tiempo real: cuando entra una solicitud B2B nueva, la lista se actualiza sola con la misma página, búsqueda y filtros.
  const { subscribe, healthy: wsHealthy } = useWebSocket();
  const refreshRef = useRef(null);
  refreshRef.current = () => fetchProformas(
    paginationModel.page + 1, paginationModel.pageSize, searchTerm, sortModel, filterModel, { silent: true }
  );
  const refreshFollowupRef = useRef(null);

  const triggerRealtimeRefresh = useCallback(() => {
    refreshRef.current?.();
    // Una segunda lectura enseguida: la proforma recién creada puede no estar confirmada en la base en el primer pedido.
    if (refreshFollowupRef.current) clearTimeout(refreshFollowupRef.current);
    refreshFollowupRef.current = setTimeout(() => refreshRef.current?.(), 1200);
  }, []);

  useEffect(() => {
    const unsubscribe = subscribe('sites.notification.created', (data) => {
      if (String(data?.type || '').toLowerCase() === 'b2b') triggerRealtimeRefresh();
    });
    return unsubscribe;
  }, [subscribe, triggerRealtimeRefresh]);

  // La campana de notificaciones también avisa cuando detecta una solicitud nueva (respaldo si el WebSocket no llega).
  useEffect(() => {
    window.addEventListener('notifications-new-orders', triggerRealtimeRefresh);
    return () => window.removeEventListener('notifications-new-orders', triggerRealtimeRefresh);
  }, [triggerRealtimeRefresh]);

  // Al recuperar el WebSocket se pide una foto actual de la lista.
  useEffect(() => {
    if (wsHealthy) triggerRealtimeRefresh();
  }, [wsHealthy, triggerRealtimeRefresh]);

  // Respaldo por reloj: 60 s con el WebSocket sano, 10 s si está caído o degradado.
  useEffect(() => {
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') refreshRef.current?.();
    }, wsHealthy ? 60_000 : 10_000);
    return () => clearInterval(interval);
  }, [wsHealthy]);

  useEffect(() => () => {
    if (refreshFollowupRef.current) clearTimeout(refreshFollowupRef.current);
  }, []);

  // Manejar cambio de paginado
  const handlePaginationChange = (newModel) => {
    setPaginationModel(newModel);
  };

  const handleSortModelChange = (newSortModel) => {
    setSortModel(newSortModel);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleFilterModelChange = (newFilterModel) => {
    const normalizedItems = (newFilterModel?.items || [])
      .filter((item) => item?.field && item?.operator)
      .slice(0, 1);

    setFilterModel({ ...newFilterModel, items: normalizedItems });
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  // Manejar cambio de búsqueda con debounce
  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearchTerm(value);
    const currentPageSize = paginationModel.pageSize;
    
    // Limpiar timeout anterior
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    // Establecer nuevo timeout para debounce
    const timeout = setTimeout(() => {
      setPaginationModel(prev => ({ ...prev, page: 0 }));
      fetchProformasRef.current(1, currentPageSize, value, sortModel, filterModel);
    }, 500);
    
    searchTimeoutRef.current = timeout;
  };

  const handleAddProforma = async (newProforma) => {
    try {
      // Usar el servicio real para crear proforma
      const createdProforma = await B2BProformaService.createProforma(newProforma);
      // Refetch para obtener los datos actualizados con paginado
      await fetchProformas(paginationModel.page + 1, paginationModel.pageSize, searchTerm, sortModel, filterModel);
    } catch (error) {
      console.error('Error creating B2B proforma:', error);
      throw error; // Re-lanzar para que el modal pueda manejar el error
    }
  };

  const handleAddFranchiseRequest = async (newRequest) => {
    try {
      await B2BProformaService.createFranchiseRequest(newRequest);
      await fetchProformas(paginationModel.page + 1, paginationModel.pageSize, searchTerm, sortModel, filterModel);
    } catch (error) {
      console.error('Error creating B2B franchise request:', error);
      throw error;
    }
  };

  const handleViewProforma = (proforma) => {
    setSelectedProforma(proforma);
    setIsViewModalOpen(true);
  };

  const handleConvertToOrder = (proforma) => {
    // Solo permitir convertir proformas con estado 'SENT'
    if (proforma.status === 'SENT') {
      setSelectedProforma(proforma);
      setIsAcceptModalOpen(true);
    }
  };

  const handleEditProforma = (proforma) => {
    setSelectedProforma(proforma);
    setIsEditModalOpen(true);
  };

  const handleSaveProformaEdit = async (proformaId, data) => {
    try {
      const updatedProforma = await B2BProformaService.updateProforma(proformaId, data);
      const normalizedProforma = updatedProforma?.data || updatedProforma?.proforma || updatedProforma;
      if (normalizedProforma?.id) {
        setProformas(prev => prev.map(proforma => (
          proforma.id === proformaId ? normalizedProforma : proforma
        )));
      } else {
        await fetchProformas(paginationModel.page + 1, paginationModel.pageSize, searchTerm, sortModel, filterModel);
      }
      SnackbarUtils.success(t('saved_successfully'));
    } catch (error) {
      console.error('Error updating B2B proforma:', error);
      SnackbarUtils.error(t('error_saving'));
      throw error;
    }
  };

  const handleRejectProforma = (proforma) => {
    setSelectedProforma(proforma);
    setIsRejectModalOpen(true);
  };

  const handleSendEmail = (proforma) => {
    setSelectedProforma(proforma);
    setIsSendEmailModalOpen(true);
  };

  const handleConfirmRejectProforma = async (proforma) => {
    try {
      const response = await B2BProformaService.rejectProforma(
        proforma.id,
        t('rejected_from_dashboard')
      );

      if (response && response.proforma) {
        setProformas(prev => prev.map(p =>
          p.id === proforma.id
            ? response.proforma
            : p
        ));
      } else {
        setProformas(prev => prev.map(p =>
          p.id === proforma.id
            ? { ...p, status: 'REJECTED', rejectedDate: new Date().toISOString() }
            : p
        ));
      }

      SnackbarUtils.success(t('proforma_rejected_successfully'));
    } catch (error) {
      console.error('Error rejecting proforma:', error);
      SnackbarUtils.error(t('error_rejecting_proforma'));
      throw error;
    }
  };

  const handleAcceptProforma = async (proforma, options) => {
    try {
      // Usar el servicio real para aceptar proforma y crear pedido automáticamente
      const result = await B2BProformaService.acceptProforma(proforma.id, options);
      
      // Actualizar estado de la proforma a "ACCEPTED"
      setProformas(prev => prev.map(p => 
        p.id === proforma.id 
          ? { ...p, status: 'ACCEPTED', acceptedDate: new Date().toISOString() }
          : p
      ));

      // Mostrar mensaje de éxito
      SnackbarUtils.success(t('order_created_successfully'));
      
    } catch (error) {
      console.error('Error creating order from proforma:', error);
      SnackbarUtils.error(t('error_creating_order'));
    }
  };


  const handleDownloadPDF = async (proforma) => {
    try {
      // Usar el servicio real para generar PDF
      const pdfBlob = await B2BProformaService.generateProformaPDF(proforma.id);
      
      // Crear URL del blob para mostrar en el visor
      const pdfUrl = window.URL.createObjectURL(pdfBlob);
      
      // Establecer la URL y abrir el modal
      setPdfUrl(pdfUrl);
      setSelectedProforma(proforma);
      setIsPDFModalOpen(true);
      
    } catch (error) {
      console.error('Error generating proforma PDF:', error);
      SnackbarUtils.error(t('error_generating_pdf'));
    }
  };

  const handleClosePDFModal = () => {
    setIsPDFModalOpen(false);
    setSelectedProforma(null);
    // Limpiar la URL del blob
    if (pdfUrl) {
      window.URL.revokeObjectURL(pdfUrl);
      setPdfUrl('');
    }
  };

  // Factura (A4) del pedido que generó la proforma entregada.
  const handleViewInvoice = async (proforma) => {
    try {
      setLoadingInvoiceId(proforma.id);
      const pdfBlob = await B2BOrderService.generateOrderPDF(proforma.generatedOrderId);
      setInvoiceUrl(window.URL.createObjectURL(pdfBlob));
      setSelectedProforma(proforma);
    } catch (error) {
      console.error('Error generating invoice PDF:', error);
      // Si la factura todavía no se emitió, el backend manda el motivo en error.message.
      SnackbarUtils.error(error.isInvoiceNotAvailable ? error.message : t('error_generating_pdf'));
    } finally {
      setLoadingInvoiceId(null);
    }
  };

  const handleCloseInvoiceModal = () => {
    if (invoiceUrl) {
      window.URL.revokeObjectURL(invoiceUrl);
    }
    setInvoiceUrl('');
    setSelectedProforma(null);
  };

  const pageTitle = isDestinationScopedView
    ? `${t('b2b_orders')} - ${destinationLocationName || `Local ${destinationLocationId}`}`
    : t('b2b_proformas');
  const pageSubtitle = isDestinationScopedView
    ? 'Solicitudes enviadas al main para validacion'
    : t('manage_b2b_proformas');

  return (
    <Box p={3}>
      <PageHeader title={pageTitle} subtitle={pageSubtitle} />
      <B2BProformaTable
        loading={loading}
        proformas={proformas}
        totalRows={totalRows}
        paginationModel={paginationModel}
        onPaginationChange={handlePaginationChange}
        sortModel={sortModel}
        onSortModelChange={handleSortModelChange}
        filterModel={filterModel}
        statusFilter={statusFilter}
        onStatusFilterChange={(value) => {
          setStatusFilter(value);
          setPaginationModel((prev) => ({ ...prev, page: 0 }));
        }}
        onFilterModelChange={handleFilterModelChange}
        searchTerm={searchTerm}
        onSearchChange={handleSearchChange}
        onRefresh={fetchProformas}
        onAdd={() => setIsAddModalOpen(true)}
        onView={handleViewProforma}
        onConvertToOrder={handleConvertToOrder}
        onEdit={handleEditProforma}
        onDownloadPDF={handleDownloadPDF}
        onViewInvoice={handleViewInvoice}
        loadingInvoiceId={loadingInvoiceId}
        onReject={handleRejectProforma}
        onSendEmail={handleSendEmail}
        showAddButton
        addButtonLabel={isDestinationScopedView ? 'Agregar' : t('add_proforma')}
        readOnlyActions={isDestinationScopedView}
      />
      {isDestinationScopedView ? (
        <FranchiseProformaRequestModal
          open={isAddModalOpen}
          onClose={() => setIsAddModalOpen(false)}
          onSave={handleAddFranchiseRequest}
          destinationLocationId={destinationLocationId}
          destinationLocationName={destinationLocationName}
        />
      ) : (
        <AddProformaModal
          open={isAddModalOpen}
          onClose={() => setIsAddModalOpen(false)}
          onSave={handleAddProforma}
        />
      )}
      <AcceptProformaModal
        open={isAcceptModalOpen}
        onClose={() => setIsAcceptModalOpen(false)}
        proforma={selectedProforma}
        onAccept={handleAcceptProforma}
      />
      <EditProformaQuantitiesModal
        open={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        proforma={selectedProforma}
        onSave={handleSaveProformaEdit}
      />
      <ViewProformaModal
        open={isViewModalOpen}
        onClose={() => setIsViewModalOpen(false)}
        proforma={selectedProforma}
      />
      <PDFViewerModal
        open={isPDFModalOpen}
        onClose={handleClosePDFModal}
        proforma={selectedProforma}
        pdfUrl={pdfUrl}
      />
      <InvoicePDFViewerModal
        open={Boolean(invoiceUrl)}
        onClose={handleCloseInvoiceModal}
        order={{ orderNumber: selectedProforma?.proformaNumber }}
        pdfUrl={invoiceUrl}
      />
      <RejectProformaModal
        open={isRejectModalOpen}
        onClose={() => setIsRejectModalOpen(false)}
        proforma={selectedProforma}
        onReject={handleConfirmRejectProforma}
      />
      <SendProformaEmailModal
        open={isSendEmailModalOpen}
        onClose={() => setIsSendEmailModalOpen(false)}
        proforma={selectedProforma}
      />
    </Box>
  );
};

export default B2BProforma;
